import React, { useState, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Editor from '@monaco-editor/react';
import { trainingGuideAPI } from '../utils/api';

const TrainingGuideEditor = ({ onSave, editingGuide }) => {
    const [formData, setFormData] = useState(editingGuide || {
        title: '',
        description: '',
        content: '',
        thumbnail: '',
        published: false
    });

    const [preview, setPreview] = useState(false);
    const [loading, setLoading] = useState(false);
    const [editorMode, setEditorMode] = useState('quill'); // 'quill' for rich text or 'html' for raw HTML
    const quillRef = useRef(null);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleContentChange = (value) => {
        setFormData(prev => ({
            ...prev,
            content: value
        }));
    };

    const handleThumbnailChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData(prev => ({
                    ...prev,
                    thumbnail: reader.result
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleThumbnailUrlChange = (e) => {
        const url = e.target.value;
        setFormData(prev => ({
            ...prev,
            thumbnail: url
        }));
    };

    const clearThumbnail = () => {
        setFormData(prev => ({
            ...prev,
            thumbnail: ''
        }));
    };

    const formatHTML = () => {
        try {
            // Parse and re-format HTML with proper indentation
            const parser = new DOMParser();
            const doc = parser.parseFromString(formData.content, 'text/html');
            
            if (doc.body.innerHTML) {
                const formatted = formatElement(doc.body, 0);
                setFormData(prev => ({
                    ...prev,
                    content: formatted.trim()
                }));
            }
        } catch (error) {
            alert('Invalid HTML content');
        }
    };

    const formatElement = (element, indent = 0) => {
        const indentStr = '  '.repeat(indent);
        let result = '';

        for (let node of element.childNodes) {
            if (node.nodeType === 3) { // Text node
                const text = node.textContent.trim();
                if (text) {
                    result += indentStr + text + '\n';
                }
            } else if (node.nodeType === 1) { // Element node
                const tagName = node.tagName.toLowerCase();
                const attrs = Array.from(node.attributes)
                    .map(attr => ` ${attr.name}="${attr.value}"`)
                    .join('');
                
                if (node.childNodes.length === 0) {
                    result += indentStr + `<${tagName}${attrs} />\n`;
                } else if (node.childNodes.length === 1 && node.childNodes[0].nodeType === 3) {
                    const text = node.childNodes[0].textContent.trim();
                    result += indentStr + `<${tagName}${attrs}>${text}</${tagName}>\n`;
                } else {
                    result += indentStr + `<${tagName}${attrs}>\n`;
                    result += formatElement(node, indent + 1);
                    result += indentStr + `</${tagName}>\n`;
                }
            }
        }

        return result;
    };

    const handleSubmit = async (published = false) => {
        setLoading(true);
        try {
            const dataToSubmit = {
                ...formData,
                published
            };

            if (editingGuide) {
                await trainingGuideAPI.update(editingGuide._id, dataToSubmit);
            } else {
                await trainingGuideAPI.create(dataToSubmit);
            }

            alert(published ? 'Guide published successfully!' : 'Guide saved as draft!');
            onSave();
        } catch (error) {
            console.error('Error saving guide:', error);
            alert('Error saving guide');
        } finally {
            setLoading(false);
        }
    };

    if (preview) {
        return (
            <div className="bg-white border border-gray-200 p-8">
                <button
                    onClick={() => setPreview(false)}
                    className="mb-6 text-black border border-black px-6 py-2 hover:bg-black hover:text-white transition-all duration-300 font-light"
                >
                    ← Back to Editor
                </button>

                <div className="max-w-4xl mx-auto">
                    {formData.thumbnail && (
                        <img src={formData.thumbnail} alt={formData.title} className="w-full h-96 object-cover mb-8 rounded" />
                    )}
                    
                    <h1 className="text-5xl font-light mb-4 tracking-tight">{formData.title || 'Untitled Guide'}</h1>
                    <p className="text-xl text-gray-600 font-light mb-8">{formData.description || 'No description'}</p>
                    
                    <div className="prose prose-lg max-w-none bg-gray-50 p-8 rounded border border-gray-200 prose-h2:text-2xl prose-h2:font-light prose-h3:text-xl prose-h3:font-light prose-p:font-light prose-li:font-light">
                        <div dangerouslySetInnerHTML={{ __html: formData.content || '<p>No content yet</p>' }} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white border border-gray-200 p-8">
            <h2 className="text-3xl font-light mb-8 tracking-tight">{editingGuide ? 'Edit Guide' : 'Create New Guide'}</h2>

            <div className="max-w-4xl space-y-8">
                {/* Title */}
                <div>
                    <label className="block text-sm font-light text-gray-700 mb-3">Title *</label>
                    <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder="Enter guide title"
                        className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:border-black font-light"
                    />
                </div>

                {/* Description */}
                <div>
                    <label className="block text-sm font-light text-gray-700 mb-3">Description *</label>
                    <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="Brief description of the guide"
                        rows="3"
                        className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:border-black font-light"
                    />
                </div>

                {/* Thumbnail */}
                <div>
                    <label className="block text-sm font-light text-gray-700 mb-3">Thumbnail *</label>
                    <div className="space-y-4">
                        {/* Upload Option */}
                        <div>
                            <p className="text-xs text-gray-600 font-light mb-2">Upload Image</p>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleThumbnailChange}
                                className="w-full px-4 py-2 border border-gray-300 focus:outline-none focus:border-black font-light"
                            />
                        </div>

                        {/* URL Option */}
                        <div>
                            <p className="text-xs text-gray-600 font-light mb-2">Or Enter Image URL</p>
                            <input
                                type="url"
                                placeholder="https://example.com/image.jpg"
                                value={formData.thumbnail && !formData.thumbnail.startsWith('data:') ? formData.thumbnail : ''}
                                onChange={handleThumbnailUrlChange}
                                className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:border-black font-light"
                            />
                        </div>

                        {/* Preview */}
                        {formData.thumbnail && (
                            <div className="flex items-end gap-4">
                                <div>
                                    <p className="text-xs text-gray-600 font-light mb-2">Preview</p>
                                    <img src={formData.thumbnail} alt="Thumbnail preview" className="w-32 h-32 object-cover rounded border border-gray-300" />
                                </div>
                                <button
                                    type="button"
                                    onClick={clearThumbnail}
                                    className="px-4 py-2 border border-red-400 text-red-600 hover:bg-red-50 transition-all font-light text-sm"
                                >
                                    Clear
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Content Editor */}
                <div>
                    <div className="flex items-center justify-between mb-3">
                        <label className="block text-sm font-light text-gray-700">Content *</label>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setEditorMode('quill')}
                                className={`px-3 py-1 text-xs font-light transition-all ${
                                    editorMode === 'quill'
                                        ? 'bg-black text-white'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                            >
                                Rich Editor
                            </button>
                            <button
                                type="button"
                                onClick={() => setEditorMode('html')}
                                className={`px-3 py-1 text-xs font-light transition-all ${
                                    editorMode === 'html'
                                        ? 'bg-black text-white'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                            >
                                HTML Editor
                            </button>
                        </div>
                    </div>

                    {editorMode === 'quill' ? (
                        <div className="bg-white border border-gray-300 rounded overflow-hidden" ref={quillRef}>
                            <ReactQuill
                                value={formData.content}
                                onChange={handleContentChange}
                                modules={{
                                    toolbar: [
                                        [{ header: [1, 2, 3, false] }],
                                        ['bold', 'italic', 'underline', 'strike'],
                                        ['blockquote', 'code-block'],
                                        [{ list: 'ordered' }, { list: 'bullet' }],
                                        ['link', 'image'],
                                        ['clean']
                                    ]
                                }}
                                formats={[
                                    'header',
                                    'bold', 'italic', 'underline', 'strike',
                                    'blockquote', 'code-block',
                                    'list', 'bullet',
                                    'link', 'image'
                                ]}
                                theme="snow"
                                placeholder="Write your guide content here..."
                                style={{ height: '400px' }}
                            />
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <Editor
                                height="500px"
                                defaultLanguage="html"
                                value={formData.content}
                                onChange={(value) => handleContentChange(value || '')}
                                theme="vs-dark"
                                options={{
                                    minimap: { enabled: false },
                                    lineNumbers: 'on',
                                    scrollBeyondLastLine: false,
                                    automaticLayout: true,
                                    formatOnPaste: true,
                                    wordWrap: 'on',
                                    fontSize: 14,
                                    tabSize: 2,
                                    fontFamily: 'Consolas, Monaco, "Courier New", monospace'
                                }}
                            />
                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    onClick={formatHTML}
                                    className="px-4 py-2 bg-gray-200 text-gray-800 hover:bg-gray-300 transition-all font-light text-sm border border-gray-300"
                                >
                                    ✨ Format HTML
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 pt-6 border-t border-gray-200">
                    <button
                        onClick={() => setPreview(true)}
                        className="px-8 py-3 border border-gray-400 text-black hover:border-black hover:bg-gray-50 transition-all duration-300 font-light tracking-wide"
                    >
                        Preview
                    </button>
                    <button
                        onClick={() => handleSubmit(false)}
                        disabled={loading}
                        className="px-8 py-3 bg-gray-200 text-black hover:bg-gray-300 transition-all duration-300 font-light tracking-wide disabled:opacity-50"
                    >
                        Save as Draft
                    </button>
                    <button
                        onClick={() => handleSubmit(true)}
                        disabled={loading}
                        className="px-8 py-3 bg-black text-white hover:bg-gray-900 transition-all duration-300 font-light tracking-wide disabled:opacity-50"
                    >
                        {loading ? 'Publishing...' : 'Publish'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TrainingGuideEditor;
